import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useBrand } from "@finitless/shared/contexts";
import { useUIStore } from "@finitless/shared/stores";
import { useSocketIdSync } from "../hooks/useSocketIdSync";
import { useOrderNotifications } from "../hooks/useOrderNotifications";
import { usePastDueBannerVisible } from "../hooks/useBilling";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import {
  Drawer,
  PastDueBanner,
  PlanUpgradeModal,
  TrialExtensionBanner,
  TrialExtensionModal,
  PAST_DUE_BANNER_HEIGHT,
} from "./ui";
import EmptyBusinessState from "./ui/States/EmptyBusinessState";
import BusinessLoadingState from "./ui/States/BusinessLoadingState";
import OrderNotificationToast from "./ui/Orders/OrderNotificationToast";
import "./ordering-shell.css";

const MOBILE_SIDEBAR_QUERY = "(max-width: 1023px)";
const MOBILE_SIDEBAR_ID = "ordering-mobile-navigation";
const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : window.matchMedia(MOBILE_SIDEBAR_QUERY).matches,
  );
  const mobileSidebarTriggerRef = useRef(null);
  const location = useLocation();
  const { initializeTheme } = useUIStore();

  // Sync socket ID with API client
  useSocketIdSync();

  // Global order notifications
  const { notifications, removeNotification } = useOrderNotifications();

  const {
    canShowBusinessContent,
    getBusinessState,
    isLoading: businessLoading,
    handleRetry,
  } = useBrand();

  const isProfilePage = location.pathname === "/account";
  const isBusinessCreationPage = location.pathname.startsWith(
    "/menu/stores/create",
  );

  // Initialize theme on component mount
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  useEffect(() => {
    const mainContent = document.querySelector("main");
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;

    const mobileQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
      if (!event.matches) setSidebarOpen(false);
    };

    setIsMobileViewport(mobileQuery.matches);
    mobileQuery.addEventListener("change", handleViewportChange);
    return () =>
      mobileQuery.removeEventListener("change", handleViewportChange);
  }, []);

  const isMobileDrawerOpen = sidebarOpen && isMobileViewport;

  // Get current page from URL path
  const currentPage = location.pathname.substring(1) || "overview";

  // Para móvil: mostrar/ocultar sidebar
  const toggleMobileSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Para desktop: colapsar/expandir sidebar
  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const showPastDueBanner = usePastDueBannerVisible();
  const [pastDueBannerHeight, setPastDueBannerHeight] = useState(0);

  return (
    <div className="ordering-shell min-h-screen" data-ordering-shell="">
      {/* Plan Upgrade modal - for Upgrade to Pro flow */}
      <PlanUpgradeModal />

      {/* Trial extension booking dialog (popup, banner, top bar, Overview card) */}
      <TrialExtensionModal />

      <div
        className="ordering-shell__frame flex h-screen"
        style={{
          transitionProperty: "none",
          paddingTop: showPastDueBanner
            ? pastDueBannerHeight || PAST_DUE_BANNER_HEIGHT
            : 0,
        }}
      >
        {/* Persistent desktop navigation */}
        {!isMobileViewport && (
          <div
            className={`ordering-shell__sidebar static inset-0 hidden translate-x-0 border-e transition-all lg:block ${isSidebarCollapsed ? "lg:w-16" : "lg:w-56"}`}
            data-sidebar-collapsed={isSidebarCollapsed ? "true" : "false"}
            data-sidebar-open="true"
          >
            <Sidebar
              onClose={closeSidebar}
              currentPage={currentPage}
              isCollapsed={isSidebarCollapsed}
            />
          </div>
        )}

        {/* Main Content Area */}
        <div
          className="flex flex-1 flex-col overflow-hidden lg:ml-0"
          data-shell-background=""
        >
          {/* Past Due banner - fixed above header, only when past_due */}
          <PastDueBanner onHeightChange={setPastDueBannerHeight} />

          {/* Trial extension offer - in normal flow, dismissible per session */}
          <TrialExtensionBanner />

          {/* Top Bar */}
          <TopBar
            onToggleSidebar={toggleSidebarCollapse}
            onToggleMobileSidebar={toggleMobileSidebar}
            mobileSidebarTriggerRef={mobileSidebarTriggerRef}
            isMobileSidebarOpen={isMobileDrawerOpen}
            mobileSidebarId={MOBILE_SIDEBAR_ID}
            isSidebarCollapsed={isSidebarCollapsed}
          />

          {/* Main Content */}
          <main
            className="bg-background flex-1 overflow-y-auto"
            data-scroll-owner="stable"
          >
            <div className="p-3 sm:p-4">
              {isProfilePage || isBusinessCreationPage ? (
                children
              ) : canShowBusinessContent ? (
                children
              ) : getBusinessState === "loading" ? (
                <BusinessLoadingState />
              ) : (
                <EmptyBusinessState
                  onRetry={handleRetry}
                  isLoading={businessLoading}
                />
              )}
            </div>
          </main>

          {/* Global Order Notifications */}
          {notifications.map((notification) => (
            <OrderNotificationToast
              key={notification.id}
              order={notification.order}
              isVisible={notification.isVisible}
              onClose={() => removeNotification(notification.id)}
              duration={5000}
              position="top-right"
              type={notification.type}
            />
          ))}
        </div>
      </div>

      {isMobileViewport && (
        <Drawer
          id={MOBILE_SIDEBAR_ID}
          isOpen={isMobileDrawerOpen}
          onClose={closeSidebar}
          aria-labelledby="ordering-sidebar-title"
          initialFocusSelector='[data-shell-action="close-sidebar"]'
          keepMounted
          className="ordering-shell__sidebar w-56 max-w-[calc(100vw-2rem)] border-e"
          backdropClassName="ordering-shell__overlay"
          backdropProps={{ "data-shell-action": "dismiss-sidebar" }}
          data-sidebar-collapsed="false"
          data-sidebar-open={isMobileDrawerOpen ? "true" : "false"}
        >
          <Sidebar
            onClose={closeSidebar}
            currentPage={currentPage}
            isCollapsed={false}
          />
        </Drawer>
      )}
    </div>
  );
};

export default AppLayout;
